import { useMemo } from 'react';
import { isUserAdminOrManager } from '../utils/global/helper';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import useUser from '../hooks/global/useUser';
import { canIndependentManageShop } from '../helpers/businesses';
import { Feature } from '../types/subscriptions';
import PageLayout from '../components/global/PageLayout';
import ItemsTab from '../components/items/ItemsTab';
import ProductsTab from '../components/items/ProductsTab';
import useInventoryEnabled from '../hooks/businesses/useInventoryEnabled';
import TabBar from '../components/filtering/TabBar';
import { FullTabInt } from '../interfaces';
import GiftCardsTab from '../components/giftCards/GiftCardsTab';
import usePersistedTab from '../hooks/global/usePersistedTab';

enum Tab {
  ITEMS,
  GIFT_CARDS,
}

export default function ItemsPage() {
  const { user } = useUser();
  const hasItemLibrary = useFeatureGuard(Feature.ITEM_LIBRARY);
  const inventoryEnabled = useInventoryEnabled();
  const canManageShop =
    !!user && (isUserAdminOrManager(user) || canIndependentManageShop(user));

  const tabs = useMemo<FullTabInt[]>(
    () => [
      {
        id: 'items',
        tabId: Tab.ITEMS,
        label: 'Items',
        component: inventoryEnabled ? <ProductsTab /> : <ItemsTab />,
        hidden: !hasItemLibrary,
      },
      {
        id: 'gift-cards',
        tabId: Tab.GIFT_CARDS,
        label: 'Gift Cards',
        component: <GiftCardsTab />,
      },
    ],
    [hasItemLibrary, inventoryEnabled],
  );

  const [tab, onChangeTab] = usePersistedTab({
    defaultTab: hasItemLibrary ? Tab.ITEMS : Tab.GIFT_CARDS,
    tabs,
  });

  return (
    <PageLayout
      title={hasItemLibrary ? 'Items & Gift Cards' : 'Gift Cards'}
      guard={{
        isAllowed: canManageShop,
      }}>
      <TabBar
        tabs={tabs}
        currentTab={tab}
        changeTab={onChangeTab}
        hideWhenOnlyOneTab
        noTopMargin
        marginTop={3}
      />
    </PageLayout>
  );
}
